import * as cheerio from 'cheerio';
import sanitize from 'sanitize-html';

/** Escapes text for safe interpolation into HTML text nodes and attribute values. */
export const escapeHtml = (text: string): string =>
  text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

export const parseLineBreaks = (html: string) => {
  return html
    .replaceAll(String.raw`\r`, '\r')
    .replaceAll(String.raw`\n`, '\n')
    .replaceAll('\r\n', '<br>')
    .replaceAll('\r', '<br>')
    .replaceAll('\n', '<br>');
};

export const trimLeadingLineBreaks = (html: string) => {
  return html.replaceAll(/^(\s|\\\\r|\\r|\\\\n|\\n)*/g, '');
};

export const sanitizePolicy = (html: string) => {
  return parseLineBreaks(
    trimLeadingLineBreaks(
      sanitize(html, {
        allowedTags: ['em', 'i', 'p', 'strong', 'a', 'br', 'b', 'sub', 'sup', 'small', 'mark'],
        allowedAttributes: {
          a: ['href'],
        },
      }),
    ),
  );
};

export const sanitizeHTML = (html: string) => sanitize(html);

const TC_MARKDOWN_ALLOWED_TAGS = [
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
] as const;

/** Safe subset of markdown HTML for T&C preview and display. */
export const sanitizeTcMarkdownHtml = (html: string) =>
  sanitize(html, {
    allowedTags: [...TC_MARKDOWN_ALLOWED_TAGS],
    allowedAttributes: {
      a: ['href', 'title'],
      th: ['colspan', 'rowspan'],
      td: ['colspan', 'rowspan'],
    },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowedSchemesByTag: {
      a: ['http', 'https', 'mailto'],
    },
  });

// Inline-formatting subset for chat messages. Differs from the T&C policy on purpose: adds
// del/s (strikethrough emitted by convertLinksToHtml) and drops headings/tables. Keep separate.
const CHAT_MESSAGE_ALLOWED_TAGS = [
  'a',
  'b',
  'br',
  'code',
  'del',
  'em',
  'i',
  'li',
  'ol',
  'p',
  's',
  'strong',
  'u',
  'ul',
] as const;

// The user digest "Good to know" headline is model-authored HTML summarizing venue chat
// activity, and it lands in an email slot that is rendered unescaped. Both the model and
// the chat content feeding it are untrusted, so allow inline emphasis only, with no
// attributes and therefore no links.
export const sanitizeDigestHeaderHtml = (html: string): string =>
  sanitize(html, {
    allowedTags: ['strong', 'em'],
    allowedAttributes: {},
  });

export const sanitizeChatMessageHtml = (html: string): string =>
  sanitize(html, {
    allowedTags: [...CHAT_MESSAGE_ALLOWED_TAGS],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
    },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowedSchemesByTag: {
      a: ['http', 'https', 'mailto'],
    },
  });

// Gate on tag-like content so plain text ('party <10', 'Tom & Jerry') stores byte-identical.
// Intentionally different from prepareChatMessageMarkdown's gate: the write path must NOT decode
// entities, the render path must. Do not unify.
export const sanitizeChatMessageContent = (content: string): string =>
  /<[a-z/!]/i.test(content) ? sanitizeChatMessageHtml(content) : content;

// Sanitize HTML and then apply DW style
export const simplifyHTML = (html: string) => {
  return sanitize(html, {
    allowedTags: [
      'em',
      'i',
      'p',
      'strong',
      'ul',
      'ol',
      'li',
      'a',
      'br',
      'b',
      'sub',
      'sup',
      'small',
      'mark',
    ],
    allowedAttributes: {
      a: ['href'],
    },
  })
    .replaceAll('<ul>', '<ul class="list-disc text-left pl-5">')
    .replaceAll('<ol>', '<ol class="list-decimal text-left pl-5">');
};

export const getTextFromHTML = (htmlString: string) => {
  const domParser = cheerio.load(htmlString);

  return domParser.text();
};
